import { Injectable } from "@angular/core";
import Keycloak from "keycloak-js";
import { Roles, Role, normalizeRole } from "./roles";
import { environment } from "../../environments/environment";
import { clearRefreshStateForCurrentUser } from "../services/refresh-state/refresh-state.service";

type KeycloakProfile = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

type SmartformsTokenParsed = {
  realm_access?: {
    roles?: string[];
  };
  resource_access?: Record<string, { roles?: string[] }>;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  email?: string;
  name?: string;
};

const PUBLIC_ROUTE_PREFIXES = [
  "#/account-complete",
  "#/public-form/",
  // A workflow link may be opened by a guest who has no account. Forcing a Keycloak login here
  // would deny them the very page that verifies them; check-sso still signs in a known user.
  "#/workflow/link/",
  "#/workflow/guest",
];

const keycloak = new Keycloak({
  url: environment.keycloak.url,
  realm: environment.keycloak.realm,
  clientId: environment.keycloak.clientId,
});

const USER_DETAILS_ENDPOINT = "/auth/user-details";
export const ASSIGNED_PLANTS_STORAGE_KEY = "assignedPlants";

let initPromise: Promise<boolean> | null = null;

function getCurrentHash(): string {
  return window.location.hash || "#/";
}

function isPublicRoute(hash = getCurrentHash()): boolean {
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => hash.startsWith(prefix));
}

function getInitOnLoad(): "login-required" | "check-sso" {
  return isPublicRoute() ? "check-sso" : "login-required";
}

function clearLegacySession(): void {
  // Clear before removing the email because refresh state is scoped by that value.
  clearRefreshStateForCurrentUser();
  localStorage.removeItem("role");
  localStorage.removeItem("firstName");
  localStorage.removeItem("lastName");
  localStorage.removeItem("email");
  localStorage.removeItem(ASSIGNED_PLANTS_STORAGE_KEY);
}

function getParsedToken(): SmartformsTokenParsed | undefined {
  return keycloak.tokenParsed as SmartformsTokenParsed | undefined;
}

function getAllRoles(): string[] {
  const parsedToken = getParsedToken();
  const realmRoles = parsedToken?.realm_access?.roles ?? [];
  const clientRoles = Object.values(parsedToken?.resource_access ?? {})
    .flatMap((clientAccess) => clientAccess?.roles ?? []);
  const allRawRoles = [...new Set([...realmRoles, ...clientRoles])];
  const normalizedAppRoles = allRawRoles
    .map((role) => normalizeRole(role))
    .filter((role): role is Role => Boolean(role));

  return [...new Set([...allRawRoles, ...normalizedAppRoles])];
}

function getPrimaryRole(): Role | undefined {
  const availableRoles = getAllRoles();

  if (availableRoles.includes(Roles.superAdmin)) {
    return Roles.superAdmin;
  }

  if (availableRoles.includes(Roles.admin)) {
    return Roles.admin;
  }

  if (availableRoles.includes(Roles.operator)) {
    return Roles.operator;
  }

  return undefined;
}

function getUsernameFromToken(): string | undefined {
  const parsedToken = getParsedToken();
  const firstName = parsedToken?.given_name?.trim();
  const lastName = parsedToken?.family_name?.trim();
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return fullName || parsedToken?.name || parsedToken?.preferred_username;
}

function syncLegacySession(profile?: KeycloakProfile): void {
  const parsedToken = getParsedToken();
  const firstName =
    profile?.firstName?.trim() || parsedToken?.given_name?.trim() || "";
  const lastName =
    profile?.lastName?.trim() || parsedToken?.family_name?.trim() || "";
  const email =
    profile?.email?.trim() || parsedToken?.email?.trim() || "";
  const role = getPrimaryRole();

  if (role) {
    localStorage.setItem("role", role);
  } else {
    localStorage.removeItem("role");
  }

  if (firstName) {
    localStorage.setItem("firstName", firstName);
  } else {
    localStorage.removeItem("firstName");
  }

  if (lastName) {
    localStorage.setItem("lastName", lastName);
  } else {
    localStorage.removeItem("lastName");
  }

  if (email) {
    localStorage.setItem("email", email);
  } else {
    localStorage.removeItem("email");
  }
}

type UserDetailsWithAssignedPlants = {
  assignedPlants?: unknown;
  message?: {
    assignedPlants?: unknown;
  };
};

export function extractAssignedPlantsFromUserDetails(
  userDetails: UserDetailsWithAssignedPlants | null | undefined
): unknown[] | undefined {
  const assignedPlants = userDetails?.message?.assignedPlants ?? userDetails?.assignedPlants;

  return Array.isArray(assignedPlants) ? assignedPlants : undefined;
}

function ensureLegacyAssignedPlantsArray(): void {
  const rawAssignedPlants = localStorage.getItem(ASSIGNED_PLANTS_STORAGE_KEY);
  if (!rawAssignedPlants) {
    localStorage.setItem(ASSIGNED_PLANTS_STORAGE_KEY, JSON.stringify([]));
    return;
  }

  try {
    const parsedAssignedPlants = JSON.parse(rawAssignedPlants);
    if (!Array.isArray(parsedAssignedPlants)) {
      localStorage.setItem(ASSIGNED_PLANTS_STORAGE_KEY, JSON.stringify([]));
    }
  } catch (_error) {
    localStorage.setItem(ASSIGNED_PLANTS_STORAGE_KEY, JSON.stringify([]));
  }
}

export async function syncLegacyAssignedPlants(token?: string): Promise<void> {
  ensureLegacyAssignedPlantsArray();

  if (!token) {
    return;
  }

  try {
    const response = await fetch(`${environment.apiUrl}${USER_DETAILS_ENDPOINT}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const responseBody = await response.text();
    if (!responseBody.trim()) {
      console.warn(
        "Keycloak session init: /auth/user-details returned an empty body. Using fallback legacy assignedPlants value."
      );
      return;
    }

    const userDetails = JSON.parse(responseBody) as UserDetailsWithAssignedPlants;
    const assignedPlants = extractAssignedPlantsFromUserDetails(userDetails);

    if (assignedPlants !== undefined) {
      localStorage.setItem(
        ASSIGNED_PLANTS_STORAGE_KEY,
        JSON.stringify(assignedPlants)
      );
      return;
    }

    console.warn(
      "Keycloak session init: assignedPlants not found in /auth/user-details response. Using fallback legacy assignedPlants value."
    );
  } catch (error) {
    console.error(
      "Keycloak session init: failed to fetch assignedPlants from /auth/user-details.",
      error
    );
  }
}

async function loadUserProfile(): Promise<KeycloakProfile | undefined> {
  if (!keycloak.authenticated) {
    return undefined;
  }

  // Token claims already provide the profile fields used in this app.
  // Avoid depending on /account profile endpoint availability.
  syncLegacySession();
  return undefined;
}

function registerKeycloakHandlers(): void {
  keycloak.onAuthSuccess = () => {
    syncLegacySession();
  };

  keycloak.onAuthRefreshSuccess = () => {
    syncLegacySession();
  };

  keycloak.onAuthLogout = () => {
    clearLegacySession();
  };

  keycloak.onTokenExpired = async () => {
    try {
      await keycloak.updateToken(30);
      syncLegacySession();
    } catch (error) {
      console.error("Failed to refresh the Keycloak token after expiry:", error);
      keycloak.clearToken();
      clearLegacySession();
    }
  };
}

export async function initKeycloak(): Promise<boolean> {
  if (initPromise) {
    return initPromise;
  }

  registerKeycloakHandlers();

  initPromise = keycloak
    .init({
      onLoad: getInitOnLoad(),
      checkLoginIframe: false,
      pkceMethod: "S256",
    })
    .then(async (authenticated: boolean) => {
      if (authenticated) {
        syncLegacySession();
        await loadUserProfile();
        await syncLegacyAssignedPlants(keycloak.token);
      } else {
        clearLegacySession();
      }

      return authenticated;
    })
    .catch((error: unknown) => {
      clearLegacySession();
      initPromise = null;
      throw error;
    });

  return initPromise!;
}

export function getKeycloakInstance(): Keycloak {
  return keycloak;
}

@Injectable({
  providedIn: "root",
})
export class KeycloakAuthService {
  getKeycloak(): Keycloak {
    return getKeycloakInstance();
  }

  async initKeycloak(): Promise<boolean> {
    return initKeycloak();
  }

  isAuthenticated(): boolean {
    return Boolean(keycloak.authenticated);
  }

  getToken(): string | undefined {
    return keycloak.token;
  }

  async login(): Promise<void> {
    await keycloak.login({
      redirectUri: window.location.href,
    });
  }

  async logout(): Promise<void> {
    clearLegacySession();
    await keycloak.logout({
      redirectUri: `${window.location.origin}${window.location.pathname}`,
    });
  }

  async updateToken(minValiditySeconds = 30): Promise<boolean> {
    if (!this.isAuthenticated()) {
      return false;
    }

    return keycloak.updateToken(minValiditySeconds);
  }

  async getUserProfile(): Promise<KeycloakProfile | undefined> {
    return loadUserProfile();
  }

  getUsername(): string | undefined {
    return getUsernameFromToken();
  }

  getPrimaryRole(): Role | undefined {
    return getPrimaryRole();
  }

  hasRole(role: string): boolean {
    return getAllRoles().includes(role);
  }

  getDefaultRoute(): string {
    return this.getPrimaryRole() === Roles.operator ? "/dashboard" : "/home";
  }

  isPublicRoute(): boolean {
    return isPublicRoute();
  }
}
